/**
 * My Login & Security — Super Admin only (iter178b).
 *
 * Location: Management → My Login & Security.
 * Route: /my-login-security.
 *
 * A non-SA session lands on the "access denied" panel; every backend
 * endpoint also enforces the same restriction so this UI cannot be
 * bypassed by URL guessing.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2, Lock, KeyRound, Shield, LogOut, ShieldAlert } from "lucide-react";


export default function MyLoginSecurity({ session }) {
  const isSA = !!session?.is_super_admin;
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Set / change password
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [otpMode, setOtpMode] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpDeliveredTo, setOtpDeliveredTo] = useState("");
  const [otpExpiresAt, setOtpExpiresAt] = useState(0);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);

  // Countdown for OTP expiry.
  useEffect(() => {
    if (!otpSent || !otpExpiresAt) return;
    const tick = () => {
      const s = Math.max(0, Math.floor((otpExpiresAt - Date.now()) / 1000));
      setOtpSecondsLeft(s);
      if (s === 0) {
        setOtpSent(false);
        setOtp("");
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [otpSent, otpExpiresAt]);

  const load = useCallback(async () => {
    if (!isSA) return;
    setLoading(true);
    try {
      const r = await api.post("/auth/security/me", { token: session.token });
      setStatus(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load security status");
    } finally { setLoading(false); }
  }, [isSA, session?.token]);

  useEffect(() => { load(); }, [load]);

  if (!isSA) {
    return (
      <div className="p-6">
        <Card className="max-w-md mx-auto border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <ShieldAlert className="w-5 h-5" /> Restricted Screen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-700">
              My Login &amp; Security is available to Super Admin accounts only.
              Please continue using OTP login on the login screen.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const requestOtp = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/security/request-setup-otp",
                                     { token: session.token });
      setOtpSent(true);
      setOtpDeliveredTo(r?.data?.delivered_to || "");
      const secs = r?.data?.expires_in_seconds || 600;
      setOtpExpiresAt(Date.now() + secs * 1000);
      const via = r?.data?.delivered_via === "email"
        ? "email" : "server console (dev)";
      toast.success(`Setup OTP sent via ${via}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to request Setup OTP");
    } finally { setBusy(false); }
  };

  const savePassword = async () => {
    if (!newPw || newPw !== confirmPw) {
      toast.error("New Password and Confirm Password must match.");
      return;
    }
    if (newPw.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    // First-time setup is implicitly OTP-verified; only "change password"
    // with an existing hash can use current_password.
    const firstTime = !status?.has_password_set;
    const useOtp = firstTime || otpMode;
    if (useOtp && !otp) {
      toast.error("Enter the OTP sent to your registered email.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/security/set-password", {
        token: session.token,
        otp: useOtp ? otp : undefined,
        current_password: !useOtp ? currentPw : undefined,
        new_password: newPw,
        confirm_password: confirmPw,
      });
      toast.success("Password saved. Password login is now enabled.");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
      setOtp(""); setOtpSent(false); setOtpMode(false);
      setOtpDeliveredTo(""); setOtpExpiresAt(0);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save password");
    } finally { setBusy(false); }
  };

  const togglePassword = async (nextEnabled) => {
    if (nextEnabled && !status?.has_password_set) {
      toast.error("Set a password first via the form below.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/security/toggle-password-login", {
        token: session.token, password_login_enabled: nextEnabled,
      });
      toast.success(nextEnabled ? "Password login enabled." : "Password login disabled.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update setting");
    } finally { setBusy(false); }
  };

  const revokeOthers = async () => {
    if (!window.confirm("Revoke every OTHER logged-in device for your account?  Your current session remains active."))
      return;
    setBusy(true);
    try {
      const r = await api.post("/auth/security/revoke-other-sessions",
                                  { token: session.token });
      toast.success(`Revoked ${r.data.revoked} other session(s).`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke sessions");
    } finally { setBusy(false); }
  };

  const forgot = async () => {
    setOtpMode(true);
    setOtpSent(false);
    setCurrentPw("");
    await requestOtp();
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="my-login-security-page">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-700" />
                My Login &amp; Security
              </CardTitle>
              <CardDescription>
                Manage your Super Admin login method.  Password login is optional; OTP always works.
                Nothing here is visible to Admin, Accounts, Franchise Owners or Center Managers.
              </CardDescription>
            </div>
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Stat label="OTP Login" value="Always Enabled" accent="green" testId="sec-tile-otp" />
            <Stat label="Password Login"
              value={status?.password_login_enabled ? "Enabled" : "Disabled"}
              accent={status?.password_login_enabled ? "purple" : "slate"}
              testId="sec-tile-pwd" />
            <Stat label="Active Sessions"
              value={status?.active_sessions ?? "—"}
              accent="slate" testId="sec-tile-sessions" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-600">
            <InfoRow label="Last Password Changed" value={fmtDT(status?.last_password_changed_at)} />
            <InfoRow label="Last Login" value={fmtDT(status?.last_login_at)} />
            <InfoRow label="Last Login IP" value={status?.last_login_ip || "—"} />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Switch checked={!!status?.password_login_enabled}
              onCheckedChange={togglePassword}
              disabled={busy || !status?.has_password_set}
              data-testid="sec-toggle-password" />
            <Label className="cursor-pointer">
              Password Login {status?.password_login_enabled ? "ON" : "OFF"}
              {!status?.has_password_set && (
                <span className="ml-2 text-[11px] text-amber-800">
                  (set a password below to enable)
                </span>
              )}
            </Label>
            <Button variant="outline" size="sm" onClick={revokeOthers}
              disabled={busy}
              className="ml-auto"
              data-testid="sec-revoke-others-btn">
              <LogOut className="w-3.5 h-3.5 mr-1.5" />
              Revoke My Other Sessions
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="w-4 h-4 text-purple-700" />
            {status?.has_password_set ? "Change Password" : "Set Password (first time)"}
          </CardTitle>
          <CardDescription>
            {status?.has_password_set
              ? "Enter your current password OR use Forgot Password to receive an OTP."
              : "First-time setup requires OTP verification.  Click Request OTP below."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 max-w-md">
          {!status?.has_password_set || otpMode ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={requestOtp} disabled={busy}
                  data-testid="sec-request-otp-btn">
                  {otpSent ? "Resend Setup OTP" : "Send Setup OTP"}
                </Button>
                {otpSent && (
                  <Badge className="bg-blue-100 text-blue-900"
                    data-testid="sec-otp-sent-badge">
                    OTP sent{otpDeliveredTo ? ` to ${otpDeliveredTo}` : ""}
                  </Badge>
                )}
                {otpSent && otpSecondsLeft > 0 && (
                  <span className="text-[11px] text-slate-600"
                    data-testid="sec-otp-countdown">
                    expires in {Math.floor(otpSecondsLeft / 60)}m{" "}
                    {String(otpSecondsLeft % 60).padStart(2, "0")}s
                  </span>
                )}
              </div>
              {otpSent && (
                <div className="space-y-1">
                  <Label>OTP</Label>
                  <Input value={otp} onChange={(e) => setOtp(e.target.value)}
                    maxLength={6} placeholder="6-digit code"
                    data-testid="sec-otp-input" />
                </div>
              )}
            </>
          ) : (
            <div className="space-y-1">
              <Label>Current Password</Label>
              <Input type="password" value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                data-testid="sec-current-pw" />
              <button type="button" onClick={forgot}
                className="text-[11px] text-blue-700 underline"
                data-testid="sec-forgot-btn">
                Forgot Password? Verify via OTP instead
              </button>
            </div>
          )}
          <div className="space-y-1">
            <Label>New Password</Label>
            <Input type="password" value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              data-testid="sec-new-pw" />
            <p className="text-[11px] text-slate-500">
              At least 8 chars, letters + digits, no repeated characters, cannot contain your mobile.
            </p>
          </div>
          <div className="space-y-1">
            <Label>Confirm New Password</Label>
            <Input type="password" value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              data-testid="sec-confirm-pw" />
          </div>
          <Button onClick={savePassword}
            disabled={
              busy || !newPw || newPw !== confirmPw
              || ((!status?.has_password_set || otpMode) && (!otpSent || !otp))
              || (status?.has_password_set && !otpMode && !currentPw)
            }
            data-testid="sec-save-pw-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
            {status?.has_password_set ? "Save New Password" : "Set Password"}
          </Button>
        </CardContent>
      </Card>

      <InspectUserCard session={session} />
    </div>
  );
}


function InspectUserCard({ session }) {
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const [saBusy, setSaBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    const m = (mobile || "").trim();
    if (!m) { toast.error("Enter a mobile"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/security/inspect-user",
                                     { token: session.token, mobile: m });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Inspection failed");
    } finally { setBusy(false); }
  };

  const toggleSa = async (targetMobile, grant) => {
    if (!window.confirm(
      grant
        ? `Grant Super Admin rights to ${targetMobile}? They will be able to change all users' rights and settings.`
        : `Revoke Super Admin rights from ${targetMobile}?`
    )) return;
    setSaBusy(true);
    try {
      const r = await api.post("/auth/security/set-super-admin", {
        token: session.token,
        target_mobile: targetMobile,
        is_super_admin: grant,
      });
      toast.success(grant ? "Super Admin granted." : "Super Admin revoked.");
      // Re-inspect to refresh state.
      setResult(null);
      const r2 = await api.post("/auth/security/inspect-user",
        { token: session.token, mobile: targetMobile });
      setResult(r2.data);
      return r;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update SA rights");
    } finally { setSaBusy(false); }
  };

  return (
    <Card className="border-amber-200">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-amber-700" />
          Inspect / Manage Super Admin Rights
        </CardTitle>
        <CardDescription>
          Diagnose why another SA cannot log in, and grant / revoke Super Admin rights.
          Read-only until you click the SA toggle.  Never shows any password or hash.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 items-end">
          <div className="space-y-1 flex-1">
            <Label>Target Mobile</Label>
            <Input value={mobile} onChange={(e) => setMobile(e.target.value)}
              placeholder="e.g. 9960886185"
              data-testid="sec-inspect-mobile" />
          </div>
          <Button onClick={run} disabled={busy}
            data-testid="sec-inspect-btn">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Inspect
          </Button>
        </div>
        {result && (
          <div className="space-y-3 pt-2 border-t">
            {result.diagnosis?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1">Diagnosis</p>
                <ul className="space-y-1">
                  {result.diagnosis.map((d, i) => (
                    <li key={i} className="text-xs px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-900"
                      data-testid={`sec-diagnosis-${i}`}>
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.manager_rows?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1">
                  Manager Rows ({result.manager_rows.length})
                </p>
                {result.manager_rows.map((r, i) => (
                  <div key={i} className="text-xs grid grid-cols-2 gap-x-4 gap-y-1 p-2 rounded border border-slate-200 bg-slate-50 mb-2">
                    <div><span className="text-slate-500">Name</span>: <strong>{r.name}</strong></div>
                    <div><span className="text-slate-500">Mobile</span>: {r.mobile}</div>
                    <div><span className="text-slate-500">Manager ID</span>: <code className="text-[10px]">{r.manager_id}</code></div>
                    <div><span className="text-slate-500">Centre</span>: {r.center}</div>
                    <div><span className="text-slate-500">Super Admin</span>: <Badge variant="outline" className={r.is_super_admin ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>{String(r.is_super_admin)}</Badge></div>
                    <div><span className="text-slate-500">Password Enabled</span>: <Badge variant="outline" className={r.password_login_enabled ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>{String(r.password_login_enabled)}</Badge></div>
                    <div><span className="text-slate-500">Password Hash</span>: <Badge variant="outline" className={r.password_hash_present ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>{r.password_hash_present ? "PRESENT" : "MISSING"}</Badge></div>
                    <div><span className="text-slate-500">Email On File</span>: <Badge variant="outline" className={r.email_present ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}>{r.email_present ? "yes" : "no"}</Badge></div>
                    <div><span className="text-slate-500">Last Login</span>: {fmtDT(r.last_login_at)}</div>
                    <div><span className="text-slate-500">Last Login Method</span>: {r.last_login_method || "—"}</div>
                    <div><span className="text-slate-500">Password Changed</span>: {fmtDT(r.last_password_changed_at)}</div>
                    <div><span className="text-slate-500">Updated At</span>: {fmtDT(r.updated_at)}</div>
                    <div className="col-span-2 pt-2 border-t border-slate-200 flex items-center gap-2">
                      <span className="text-slate-500">Super Admin rights:</span>
                      {r.is_super_admin ? (
                        <Button variant="outline" size="sm"
                          onClick={() => toggleSa(r.mobile, false)}
                          disabled={saBusy || r.mobile === session.mobile}
                          data-testid="sec-revoke-sa-btn"
                          title={r.mobile === session.mobile ? "You cannot revoke your own rights" : ""}>
                          Revoke Super Admin
                        </Button>
                      ) : (
                        <Button size="sm"
                          onClick={() => toggleSa(r.mobile, true)}
                          disabled={saBusy}
                          data-testid="sec-grant-sa-btn">
                          {saBusy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                          Grant Super Admin
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {result.other_hash_holders?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-red-700 mb-1">
                  ⚠ Other accounts holding password hashes ({result.other_hash_holders.length})
                </p>
                {result.other_hash_holders.map((r, i) => (
                  <div key={i} className="text-xs p-2 rounded border border-red-200 bg-red-50 mb-1">
                    {r.name} · {r.mobile} · SA: {String(r.is_super_admin)} · changed {fmtDT(r.last_password_changed_at)}
                  </div>
                ))}
              </div>
            )}
            {result.recent_events?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1">Recent Security Events</p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {result.recent_events.map((e, i) => (
                    <div key={i} className="text-[11px] px-2 py-1 rounded bg-slate-50 border border-slate-100">
                      <strong>{e.action}</strong>
                      {e.reason_code ? ` · ${e.reason_code}` : ""}
                      <span className="text-slate-500 ml-2">{fmtDT(e.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function Stat({ label, value, accent, testId }) {
  const map = {
    purple: "bg-purple-50 border-purple-200 text-purple-800",
    green: "bg-emerald-50 border-emerald-200 text-emerald-800",
    slate: "bg-slate-50 border-slate-200 text-slate-800",
  };
  return (
    <div className={`p-3 rounded-lg border ${map[accent] || map.slate}`} data-testid={testId}>
      <p className="text-[11px] uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-lg font-semibold mt-1">{value}</p>
    </div>
  );
}


function InfoRow({ label, value }) {
  return (
    <div className="p-2 rounded-md border border-slate-100 bg-white">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-sm text-slate-800 mt-0.5">{value || "—"}</p>
    </div>
  );
}


function fmtDT(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch { return v; }
}
